/**
 * Put off one dose before it rings — docs/33 C9.
 *
 * "I'll take tonight's at 10 instead of 8": the dose moves, the statement
 * says so, and the caregivers' window moves with it, because the new dose
 * is due at 10 and is late from 10. Unlike Later (snooze.ts), which is
 * pressed on a reminder that has rung and never moves the escalation clock.
 *
 * One rule for the server (which enforces it) and the phone (which offers
 * only what it allows):
 *
 *  - Only a dose still to come: once it has rung, Later is the answer.
 *  - Later than it was first due, never earlier: earlier is "I took it".
 *    Its first time again is allowed, and puts it back as it was.
 *  - The same day, and before the same medicine's next dose: two of them
 *    must never end up due together.
 *  - At most three hours after it was first due; a critical medicine one
 *    hour. Past that it is a different instruction, and the doctor's.
 */
const MINUTE = 60_000;

export interface PostponeState {
  /** When the dose is due now (its new time, if already put off). */
  dueAt: number;
  /** When it was first due: `postponedFrom`, or `dueAt` if never put off. */
  firstDueAt: number;
  now: number;
  /** The end of the dose's own day, in the person's time zone (exclusive). */
  dayEnd: number;
  /** The same medicine's next dose that day, if any (not this one, not a `moved` one). */
  nextDueAt: number | null;
  isCritical: boolean;
}

export type PostponeRefusal = 'already_due' | 'not_later' | 'too_late' | 'after_next' | 'past';

export type PostponeDecision =
  | { ok: true; until: number; restore: boolean }
  | { ok: false; reason: PostponeRefusal };

/** How far after its first time a dose may go. */
export function postponeMaxMinutes(isCritical: boolean): number {
  return isCritical ? 60 : 180;
}

/** The latest time a dose may be put off to, or null when it may not be put off at all. */
export function latestPostpone(state: PostponeState): number | null {
  if (state.dueAt <= state.now) return null;
  const caps = [state.firstDueAt + postponeMaxMinutes(state.isCritical) * MINUTE, state.dayEnd - MINUTE];
  if (state.nextDueAt !== null) caps.push(state.nextDueAt - MINUTE);
  const latest = Math.min(...caps);
  return latest > state.firstDueAt && latest > state.now ? latest : null;
}

export function decidePostpone(state: PostponeState, until: number): PostponeDecision {
  const at = Math.floor(until / MINUTE) * MINUTE;
  if (state.dueAt <= state.now) return { ok: false, reason: 'already_due' };
  if (at <= state.now) return { ok: false, reason: 'past' };
  if (at < state.firstDueAt) return { ok: false, reason: 'not_later' };
  if (at === state.firstDueAt) return { ok: true, until: at, restore: true };
  if (state.nextDueAt !== null && at >= state.nextDueAt) return { ok: false, reason: 'after_next' };
  const latest = latestPostpone(state);
  if (latest === null || at > latest) return { ok: false, reason: 'too_late' };
  return { ok: true, until: at, restore: false };
}

/**
 * What the phone offers: half an hour, an hour, two and three after the
 * first time (a critical medicine: half an hour and an hour), whichever the
 * rule allows, and the first time again if it was put off. No clock face.
 */
export function postponeChoices(state: PostponeState): Array<{ at: number; restore: boolean }> {
  const steps = state.isCritical ? [30, 60] : [30, 60, 120, 180];
  const out: Array<{ at: number; restore: boolean }> = [];
  if (state.dueAt !== state.firstDueAt) {
    const back = decidePostpone(state, state.firstDueAt);
    if (back.ok) out.push({ at: back.until, restore: true });
  }
  for (const m of steps) {
    const at = state.firstDueAt + m * MINUTE;
    if (at === state.dueAt) continue;
    const d = decidePostpone(state, at);
    if (d.ok) out.push({ at: d.until, restore: false });
  }
  return out;
}
